import { useApi } from './api';
import type { CaptureFormData } from '../types/CaptureFormData';

export const useReferralService = () => {
  const { apiFetch } = useApi();

  const createReferral = async (data: CaptureFormData) => {
    const res = await apiFetch('/referrals', { // ✅ CAMBIADO: URL relativa
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      // ✅ NUEVO: Preservar el response y el mensaje del backend
      const errorData = await res.json().catch(() => ({}));
      const error = new Error(errorData.message || 'Error al crear referral');
      (error as any).response = res;
      (error as any).status = res.status;
      throw error;
    }
    return res.json();
  };

  const updateReferral = async (id: number, data: CaptureFormData) => {
    const res = await apiFetch(`/referrals/${id}`, { // ✅ CAMBIADO: URL relativa
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      // ✅ NUEVO: Preservar el response y el mensaje del backend
      const errorData = await res.json().catch(() => ({}));
      const error = new Error(errorData.message || 'Error al actualizar referral');
      (error as any).response = res;
      (error as any).status = res.status;
      throw error;
    }
    return res.json();
  };

  const enviarReferral = async (id: number) => {
    const res = await apiFetch(`/referrals/${id}/enviar`, { // ✅ CAMBIADO: URL relativa
      method: 'POST',
    });
    if (!res.ok) throw new Error('Error al enviar referral');
    return res.json();
  };

  // ✅ ACTUALIZADO: Función con soporte para filtros
  const getReferrals = async (page = 0, size = 10, filters?: any) => {
    // Construir query parameters dinámicamente
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });

    // Agregar filtros si están presentes
    if (filters?.search && filters.search.trim()) {
      params.append('search', filters.search.trim());
    }

    if (filters?.status && filters.status !== 'ALL') {
      params.append('status', filters.status);
    }

    if (filters?.product && filters.product !== 'ALL') {
      params.append('product', filters.product);
    }

    // ✅ CORREGIDO: Siempre enviar el parámetro origin (incluso si es 'ALL')
    if (filters?.origin) {
      params.append('origin', filters.origin);
    }

    if (filters?.specialistId != null && filters.specialistId !== '' && filters.specialistId !== 'ALL') {
      params.append('specialistId', String(filters.specialistId));
    }

    if (filters?.unassigned === true) {
      params.append('unassigned', 'true');
    }

    if (filters?.assignmentStatus && filters.assignmentStatus !== 'ALL') {
      params.append('assignmentStatus', filters.assignmentStatus);
    }

    if (filters?.readyForSync === true) {
      params.append('readyForSync', 'true');
    } else if (filters?.readyForSync === false) {
      params.append('readyForSync', 'false');
    }

    if (filters?.forSyncQueue === true) {
      params.append('forSyncQueue', 'true');
    } else if (filters?.forSyncQueue === false) {
      params.append('forSyncQueue', 'false');
    }

    const url = `/referrals?${params.toString()}`; // ✅ CAMBIADO: URL relativa
    console.log('Fetching referrals with URL:', url);
    
    const res = await apiFetch(url);
    console.log('Fetching referrals response:', res);
    
    if (!res.ok) throw new Error('No se pudo obtener la lista de referrals');
    return res.json();
  };

  // NUEVO: Método para obtener un referral específico por ID
  const getReferralById = async (id: number) => {
    const res = await apiFetch(`/referrals/${id}`); // ✅ CAMBIADO: URL relativa
    if (!res.ok) throw new Error('No se pudo obtener el referral');
    return res.json();
  };

  const getIntakeContext = async (id: number) => {
    const res = await apiFetch(`/referrals/${id}/intake-context`);
    if (!res.ok) throw new Error('No se pudo obtener el contexto del intake');
    return res.json();
  };

  const getDashboardStats = async (): Promise<{
    total: number;
    pendientes: number;
    sinCitas: number;
    sincronizados: number;
    errores: number;
    canalizados?: number;
    sinCanalizar?: number;
  }> => {
    const res = await apiFetch('/referrals/dashboard/stats');
    if (!res.ok) throw new Error('No se pudieron obtener las estadísticas');
    return res.json();
  };

  const markReferralsReadyForSync = async (referralIds: number[]) => {
    const res = await apiFetch('/referrals/mark-ready-for-sync', {
      method: 'POST',
      body: JSON.stringify({ referralIds }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Error al marcar casos como listos para sincronizar');
    }
    return data;
  };

  return { 
    createReferral, 
    updateReferral,
    enviarReferral, 
    getReferrals, 
    getReferralById,
    getIntakeContext,
    getDashboardStats,
    markReferralsReadyForSync
  };
};